import { ImageWithFallback } from '@/components/ui/ImageWithFallback';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useMaterialEditStore } from '@/screens/MaterialEdit/store';
import { campaignDisplayFormatMapper } from '@/utils/mapper';
import { cn } from '@/utils/utils';
import { EDisplayFormat } from '@shelflix/schemas/_common/enum';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@shelflix/ui-web/components/ui/dialog';
import { useRef, useState, useEffect } from 'react';
import { ScheduleMaterialModal } from '../ScheduleCampaignModal';

interface CampaignCardProps {
  campaign: CampaignDto;
  showExpiration?: boolean;
  showTitle?: boolean;
  showScheduled?: boolean;
  showSetPlayNow?: boolean;
  showSetSchedule?: boolean;
  showSetRemoveSchedule?: boolean;
  showSetAsDefault?: boolean;
  showPreview?: boolean;
  isDefault?: boolean;
  isScheduled?: boolean;
  isPlaying?: boolean;
  actions?: {
    onClick?: () => void;
    onCloseModal?: () => void;
    onSetPlayNow?: () => void;
    onSetSchedule?: (campaign: CampaignDto, startDate: string, endDate: string) => void;
    onSetAsDefault?: () => void;
    onSetRemoveSchedule?: (campaign: CampaignDto) => void;
  };
}

export function CampaignCard({ campaign, showTitle = true, actions, ...props }: CampaignCardProps) {
  const { isDarkMode } = useDarkMode();
  const { scheduledCampaigns } = useMaterialEditStore();

  const [imageSrc, setImageSrc] = useState(campaign?.campaignMedias[0]);
  const [thumbnailSrc, setThumbnailSrc] = useState(campaign.thumbnailUrl);

  // Sync state when campaign prop changes (e.g., on refetch with new presigned URLs)
  // ImageWithFallback handles the URL comparison intelligently to prevent layout shifts
  useEffect(() => {
    setImageSrc(campaign?.campaignMedias[0]);
    setThumbnailSrc(campaign.thumbnailUrl);
  }, [campaign?.campaignMedias, campaign.thumbnailUrl]);

  const handleSetAsDefault = () => {
    actions?.onSetAsDefault?.();
  };

  const handlePlayNow = () => {
    actions?.onSetPlayNow?.();
  };

  const VideoPreview = () => {
    const [open, setOpen] = useState<boolean>(false);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const videos = campaign.campaignMedias.map((media) => media.mediaUrl || '');

    useEffect(() => {
      if (open) {
        setCurrentIndex(0);
      }
    }, [open]);

    const handleEnded = () => {
      if (currentIndex < videos.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        setOpen(false);
      }
    };
    return (
      <div className="relative w-full overflow-hidden rounded-md bg-black">
        <div className="aspect-video">
          {['mp4', 'mov', 'webm'].includes(imageSrc?.extension ?? '') ? (
            <div className="relative aspect-video bg-black">
              <video
                ref={videoRef}
                src={videos[currentIndex]}
                poster={thumbnailSrc || 'https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png'}
                controls
                muted
                onEnded={handleEnded}
                className="aspect-video h-auto w-full rounded"
              />
              <p className="text-muted-foreground mt-2 text-center text-sm">
                Vídeo {currentIndex + 1} de {videos.length}
              </p>
            </div>
          ) : (
            <img
              className="h-full w-full object-cover"
              alt={campaign.name + ' logo'}
              src={thumbnailSrc || 'https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png'}
            />
          )}
        </div>
      </div>
    );
  };

  const CampaignInfo = () => (
    <div className="p-4">
      <div className="mb-3">
        <h3 className="mb-4 text-lg font-semibold text-white">{campaign.name}</h3>
        <div className="mt-2 flex flex-col items-center text-sm text-gray-300">
          <div className="flex w-full flex-row justify-between">
            <span>Formato:</span>
            <span>{campaignDisplayFormatMapper(campaign.displayFormat as EDisplayFormat)}</span>
          </div>
          <div className="flex w-full flex-row justify-between">
            <span>Marca:</span>
            <span>{campaign.brand.name}</span>
          </div>
          <div className="flex w-full flex-row justify-between">
            <span>Data de início:</span>
            {(props.showSetRemoveSchedule || props.showScheduled) &&
            scheduledCampaigns.find((c) => c.campaign.uuid === campaign.uuid)?.startDate
              ? new Date(
                  scheduledCampaigns.find((c) => c.campaign.uuid === campaign.uuid)?.startDate
                ).toLocaleDateString()
              : campaign.startDate
                ? new Date(campaign.startDate).toLocaleDateString()
                : ''}
          </div>
          <div className="flex w-full flex-row justify-between">
            <span>Data de fim:</span>
            <span>
              {(props.showSetRemoveSchedule || props.showScheduled) &&
              scheduledCampaigns.find((c) => c.campaign.uuid === campaign.uuid)?.endDate
                ? new Date(
                    scheduledCampaigns.find((c) => c.campaign.uuid === campaign.uuid)?.endDate
                  ).toLocaleDateString()
                : campaign.endDate
                  ? new Date(campaign.endDate).toLocaleDateString()
                  : 'Indefinido'}
            </span>
          </div>
        </div>
      </div>

      {actions && (
        <div className="mt-4 flex flex-wrap gap-2">
          {props.showSetPlayNow && (
            <Button
              size="sm"
              onClick={handlePlayNow}
              disabled={props.isPlaying}
              className={`w-full flex-1 text-white ${props.isPlaying ? 'bg-green-600 hover:bg-green-700' : ''}`}
            >
              {/* <Play className="mr-2 h-4 w-4" /> */}
              <Icon name="arrow_upload_progress" size={28} />

              {props.isPlaying ? 'Passando' : 'Confirmar troca de campanha'}
            </Button>
          )}
          {props.showSetRemoveSchedule && (
            <>
              <Button
                size="sm"
                onClick={() => {
                  actions?.onSetPlayNow?.();
                }}
                type="button"
                disabled={props.isPlaying}
                className={`w-full`}
              >
                {/* <Play className="mr-2 h-4 w-4" /> */}
                <Icon name="auto_read_play" size={28} />

                {'Passar agora'}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  actions?.onSetRemoveSchedule?.(campaign);
                }}
                disabled={props.isPlaying}
                className={`w-full flex-1 bg-red-600 text-white hover:bg-red-700`}
              >
                {/* <Play className="mr-2 h-4 w-4" /> */}
                <Icon name="arrow_upload_progress" size={28} />

                {'Remover campanha agendada'}
              </Button>
            </>
          )}
          {props.showSetAsDefault && (
            <Button
              size="sm"
              variant={'default'}
              onClick={handleSetAsDefault}
              className={`flex-1 ${props.isDefault ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-yellow-600'}`}
            >
              <Icon name="award_star" className="text-[#2D2D2D]" fill={1} size={20} />

              {props.isDefault ? 'Padrão' : 'Confirmar campanha padrão'}
            </Button>
          )}
        </div>
      )}
    </div>
  );

  const Thumbnail = () => (
    <div
      className={cn(
        'campaign-card-shadow relative w-full overflow-hidden rounded-md',
        isDarkMode ? 'bg-gray-900/50' : 'bg-black'
      )}
      onClick={actions?.onClick}
    >
      <ImageWithFallback
        src={thumbnailSrc}
        alt={campaign.name + ' thumbnail'}
        aspectRatio="aspect-[9/16]"
        objectFit="contain"
        containerClassName="w-full"
        skeletonClassName="bg-gray-700/50"
      />

      <div className="absolute right-0 bottom-0 left-0 mt-5 bg-gradient-to-t from-black/50 to-transparent p-3">
        {showTitle && <h3 className="line-clamp-2 text-xs font-medium text-white">{campaign.name}</h3>}
      </div>
    </div>
  );

  return (
    <>
      <div onClick={actions?.onClick} className="cursor-pointer">
        <Thumbnail />
      </div>

      <Dialog
        open={props.showPreview}
        onOpenChange={(status) => {
          if (!status) {
            actions?.onCloseModal?.();
          }
        }}
      >
        <DialogContent className="max-w-2xl gap-4 border-gray-800 bg-black/95 text-white" aria-describedby="modal">
          <DialogClose asChild className="absolute top-4 right-4 cursor-pointer">
            <Icon name="close" size={32} className="cursor-pointer text-red-500" />
          </DialogClose>
          <DialogHeader>
            <div className="flex w-full items-center justify-center">
              <Icon
                name="auto_read_play"
                fill={1}
                size={32}
                className="rounded-md bg-[var(--primary)]/30 p-2 text-[var(--primary)]"
              />
            </div>
            <DialogTitle className="mt-4 mb-4 text-white">Preview</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <VideoPreview />
            <CampaignInfo />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={props.showSetPlayNow}
        onOpenChange={(status) => {
          if (!status) {
            actions?.onCloseModal?.();
          }
        }}
      >
        <DialogContent className="max-w-2xl gap-4 border-gray-800 bg-black/95 text-white" aria-describedby="modal">
          <DialogClose asChild className="absolute top-4 right-4 cursor-pointer">
            <Icon name="close" size={32} className="text-red-500" />
          </DialogClose>
          <DialogHeader>
            <div className="flex w-full items-center justify-center">
              <Icon
                name="change_circle"
                fill={1}
                size={32}
                className="rounded-md bg-[var(--primary)]/30 p-2 text-[var(--primary)]"
              />
            </div>
            <DialogTitle className="mt-4 mb-4 text-white">Trocar campanha</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <VideoPreview />
            <CampaignInfo />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={props.showSetAsDefault}
        onOpenChange={(status) => {
          if (!status) {
            actions?.onCloseModal?.();
          }
        }}
      >
        <DialogContent className="max-w-2xl gap-4 border-gray-800 bg-black/95 text-white">
          <DialogClose asChild className="absolute top-4 right-4 cursor-pointer">
            <Icon name="close" size={32} className="text-red-500" />
          </DialogClose>
          <DialogHeader>
            <div className="flex w-full items-center justify-center">
              <Icon name="award_star" fill={1} size={32} className="rounded-md bg-gray-400/30 p-2 text-gray-400" />
            </div>
            <DialogTitle className="mt-4 mb-4 text-white">Vincular campanha padrão</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <VideoPreview />
            <CampaignInfo />
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={props.showSetRemoveSchedule}
        onOpenChange={(status) => {
          if (!status) {
            actions?.onCloseModal?.();
          }
        }}
      >
        <DialogContent className="max-w-2xl gap-4 border-gray-800 bg-black/95 text-white">
          <DialogClose asChild className="absolute top-4 right-4 cursor-pointer">
            <Icon name="close" size={32} className="text-red-500" />
          </DialogClose>
          <DialogHeader>
            <div className="flex w-full items-center justify-center">
              <Icon
                name="calendar_add_on"
                fill={1}
                size={32}
                className="rounded-md bg-[var(--primary)]/30 p-2 text-[var(--primary)]"
              />
            </div>
            <DialogTitle className="mt-4 mb-4 text-white">Campanha agendada</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <VideoPreview />
            <CampaignInfo />
          </div>
        </DialogContent>
      </Dialog>

      <ScheduleMaterialModal
        openModal={props.showSetSchedule}
        campaign={campaign}
        scheduleCampaigns={scheduledCampaigns}
        onOpenChange={(status) => {
          if (!status) {
            actions?.onCloseModal?.();
          }
        }}
      >
        <VideoPreview />
      </ScheduleMaterialModal>
    </>
  );
}
